import React, { useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import {
    FormControl,
    FormLabel,
    VStack,
    useToast,
    Box,
    Text,
    SimpleGrid,
    Spinner,
    Grid,
    GridItem,
    Image,
    IconButton,
    Center,
    Textarea
} from '@chakra-ui/react';
import FormActionLabel from "../../../components/Styled/FormActionLabel";
import CustomAlert from "../../../components/Styled/StyledAlert";
import { ButtonStack, CustomButton } from "../../../components/Styled/StyledButtons";
import FieldControl from '../../../components/Styled/FieldControl';
import { useDrawer } from '../../../Context/DrawerContext/DrawerContext';
import { Formik, Form, Field } from 'formik';
import ConfirmDialog from "../../../Context/ConfirmDialog/ConfirmDialog";
import MarkdownEditor from '../../../components/Markdown/MarkdownEditor';
import { addNews } from "../NewsAPI";
import SectionHeading from "../../../components/SectionHeading/SectionHeading";
import utils from "../../../utils/commonFunctions";
import { CloseIcon } from '@chakra-ui/icons';

const NewsAddForm = ({ refetchNewsData }) => {
    const { closeDrawer } = useDrawer();
    const [descriptionValue, setDescriptionValue] = useState('');
    const toast = useToast({ position: 'top-right' });
    const [loading, setLoading] = useState(false);
    const [embedCode, setEmbedCode] = useState('');
    const [images, setImages] = useState([]);
    const [uploadingIds, setUploadingIds] = useState([]);
    const formikPropsRef = useRef(null);

    const newschema = {
        title: '',
        description: '',
        images: [],
        videoEmbed: ''
    };

    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        if (!values.title || !values.description) {
            toast({
                title: !values.title ? 'News title is required' : 'News description is required',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        try {
            await addNews(JSON.stringify(values));
            toast({
                title: 'News added successfully',
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
            resetForm();
            setImages([]);
            refetchNewsData();
            closeDrawer();
        } catch (error) {
            toast({
                title: error.response ? 'Error adding news' : error.request ? 'Network Error' : 'Error',
                description: error.response?.data?.message || error.message || 'Unexpected error',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const requestConfirm = (options) => setConfirmDialog({ ...options, isOpen: true });
    const handleConfirm = () => setConfirmDialog(prev => { prev.onConfirm(); return { ...prev, isOpen: false }; });
    const handleCancelDialog = () => setConfirmDialog(prev => ({ ...prev, isOpen: false }));

    const handleDrop = async (acceptedFiles) => {
        const setFieldValue = formikPropsRef.current?.setFieldValue;
        const tempIds = acceptedFiles.map(() => uuidv4());
        setUploadingIds(prev => [...prev, ...tempIds]);

        const uploaded = [];
        for (let index = 0; index < acceptedFiles.length; index++) {
            const file = acceptedFiles[index];
            const tempId = tempIds[index];
            const sizeMB = (file.size / (1024 * 1024)).toFixed(2);

            if (file.size > 2097152 || !['image/jpeg', 'image/png'].includes(file.type)) {
                toast({
                    title: 'Invalid File',
                    description: `${file.name} is ${sizeMB}MB. Allowed size is up to 2MB and only JPEG/PNG formats.`,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                setUploadingIds(prev => prev.filter(id => id !== tempId));
                continue;
            }

            try {
                const res = await utils.handleFileUpload(file, `news/images/${Date.now()}`);
                if (res?.data?.fileUrl) {
                    uploaded.push({ id: tempId, preview: res.data.fileUrl, fileUrl: res.data.fileUrl });
                }
            } catch (error) {
                toast({
                    title: 'Upload failed',
                    description: `Could not upload ${file.name}`,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setUploadingIds(prev => prev.filter(id => id !== tempId));
            }
        }

        const updated = [...images, ...uploaded];
        setImages(updated);
        setFieldValue && setFieldValue('images', updated.map(img => img.fileUrl));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop
    });

    const handleDelete = (formikProps, index) => {
        const updated = images.filter((_, i) => i !== index);
        setImages(updated);
        formikProps.setFieldValue('images', updated.map(img => img.fileUrl));
    };

    return (
        <>
            <Formik initialValues={newschema} onSubmit={handleSubmit}>
                {formikProps => {
                    formikPropsRef.current = formikProps;

                    return (
                        <Form>
                            <VStack spacing={4} align="stretch">
                                <FormActionLabel formAction="add" formName="News" />
                                <SimpleGrid columns={1} gap={4} px={2} py={4}>
                                    <FieldControl isRequired={true} formikProps={formikProps} name="title" label="Title" placeholder="Enter news title" />
                                    <FormControl isRequired={true}>
                                        <FormLabel htmlFor="description">Description</FormLabel>
                                        <MarkdownEditor value={formikProps.values.description} onChange={val => formikProps.setFieldValue('description', val)} />
                                    </FormControl>

                                    <SectionHeading icon="mdi:account-file-text" text="Upload News Images" />
                                    <CustomAlert status="info" message="Allowed file types: JPEG, PNG. Max file size: 2MB." />
                                    <Grid templateColumns="repeat(12, 1fr)" gap={1} color="blue.300" px={4}>
                                        <GridItem colSpan={{ base: 12, md: 12 }}>
                                            <FormControl>
                                                <Box {...getRootProps()} border="2px dashed #ccc" padding="20px" textAlign="center" cursor="pointer" borderRadius="md" bg={isDragActive ? 'blue.50' : 'white'}>
                                                    <input {...getInputProps()} />
                                                    <Text>{isDragActive ? 'Drop the files here ...' : "Drag 'n' drop or click to select images"}</Text>
                                                </Box>
                                            </FormControl>

                                            {images.length > 0 ? (
                                                <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={4} pt={4}>
                                                    {images.map((image, index) => (
                                                        <Box key={image.id} position="relative" bg="gray.100" p={1} rounded="md" shadow="md">
                                                            {uploadingIds.includes(image.id) ? (
                                                                <Center boxSize="100px">
                                                                    <Spinner size="md" color="blue.500" />
                                                                </Center>
                                                            ) : (
                                                                <Image src={image.preview} alt={`Uploaded ${index}`} boxSize="100px" objectFit="cover" />
                                                            )}
                                                            {!uploadingIds.includes(image.id) && (
                                                                <IconButton
                                                                    icon={<CloseIcon />}
                                                                    size="xs"
                                                                    aria-label="Delete image"
                                                                    position="absolute"
                                                                    bg="tomato"
                                                                    top="2px"
                                                                    right="2px"
                                                                    onClick={() => handleDelete(formikProps, index)}
                                                                />
                                                            )}
                                                        </Box>
                                                    ))}
                                                </SimpleGrid>
                                            ) : (
                                                <Center py={4}><Text fontSize="sm" color="gray.500">No images uploaded yet</Text></Center>
                                            )}

                                        </GridItem>
                                    </Grid>

                                    <CustomAlert status="info" message="This section is for news images." />

                                    <GridItem>
                                        <FormControl isInvalid={formikProps.errors.videoEmbed && formikProps.touched.videoEmbed}>
                                            <FormLabel htmlFor="videoEmbed">Embed Video Code</FormLabel>
                                            <Field name="videoEmbed">
                                                {({ field }) => (
                                                    <Textarea {...field} id="videoEmbed" placeholder="Enter Embed Code" borderWidth="2px" focusBorderColor="#63B3ED" />
                                                )}
                                            </Field>
                                            {formikProps.errors.videoEmbed && formikProps.touched.videoEmbed && (
                                                <Text color="red.500" fontSize="sm">{formikProps.errors.videoEmbed}</Text>
                                            )}
                                            {formikProps.values.videoEmbed && (
                                                <Box mt="4">
                                                    <Text fontSize="md" mb="2">Preview:</Text>
                                                    <Box as="iframe" srcDoc={formikProps.values.videoEmbed} width="100%" height="200px" frameBorder="0" allowFullScreen></Box>
                                                </Box>
                                            )}
                                        </FormControl>
                                    </GridItem>
                                    <CustomAlert status="info" message="Please enter the YouTube embed code. Find it via 'Share' > 'Embed' on YouTube." />
                                </SimpleGrid>
                                <AlertAndButtons formikProps={formikProps} closeDrawer={closeDrawer} requestConfirm={requestConfirm} />
                            </VStack>
                        </Form>
                    );
                }}
            </Formik>
            <ConfirmDialog isOpen={confirmDialog.isOpen} onClose={handleCancelDialog} onConfirm={handleConfirm} title={confirmDialog.title} message={confirmDialog.message} />
        </>
    );
};

const AlertAndButtons = ({ formikProps, closeDrawer, requestConfirm }) => {
    const handleCancel = () => {
        requestConfirm({
            title: "Cancel Confirmation",
            message: "Are you sure you want to cancel? Any unsaved changes will be lost.",
            onConfirm: () => {
                formikProps.handleReset();
                closeDrawer();
            }
        });
    };

    const handleReset = () => {
        requestConfirm({
            title: "Reset Confirmation",
            message: "Are you sure you want to reset all your changes?",
            onConfirm: () => {
                formikProps.handleReset();
            }
        });
    };

    return (
        <Box flexDirection="column" alignItems="left" style={{ alignItems: 'flex-start' }}>
            <CustomAlert status="warning" message="If you want to abort the action, please use the Cancel button." />
            <ButtonStack direction="row" style={{ justifyContent: 'flex-start' }}>
                <CustomButton onClick={handleCancel} type="cancel" showIcon={false}>
                    Cancel
                </CustomButton>
                <CustomButton onClick={handleReset} type="reset">
                    Reset
                </CustomButton>
                <CustomButton
                    type="submit"
                    disabled={formikProps.isSubmitting}
                    style={{
                        opacity: formikProps.isSubmitting ? 0.7 : 1,
                        pointerEvents: formikProps.isSubmitting ? 'none' : 'auto',
                    }}
                >
                    {formikProps.isSubmitting ? (
                        <>
                            <Spinner size="xs" mr={2} />
                            Submitting...
                        </>
                    ) : 'Submit'}
                </CustomButton>
            </ButtonStack>
        </Box>
    );
};

export default NewsAddForm;
